import { useState } from "react";
import axios from "axios";
import { Loader2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { openIntake, track } from "@/lib/ebook";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASONS = [
  "Royal Essence", "Sovereign Canvas", "Majestic Vibes",
  "Help Choosing an Experience", "Existing Order",
  "Corporate / Business Artwork", "Partnership", "Media", "Other",
];

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState(REASONS[0]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name, email,
        message: `[${reason}]${phone ? ` (phone: ${phone})` : ""}\n\n${message}`,
      });
      toast.success("Your message is in. We'll reply within 24 hours.");
      track("contact_form_completed", "contact-page", { reason });
      setName(""); setEmail(""); setPhone(""); setMessage(""); setReason(REASONS[0]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <SEO
        title="Contact Crowned Canvas Creations | Madison, Wisconsin"
        description="Contact Crowned Canvas Creations in Madison, Wisconsin about Royal Essence, Sovereign Canvas, Majestic Vibes, existing orders, corporate artwork or partnerships."
        path="/contact"
      />
      <Header />
      <main className="pt-24 md:pt-32">
        <section className="relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_15%_30%,rgba(130,73,55,0.25),transparent_60%)]" aria-hidden />
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-14 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="overline">Contact</p>
              <h1 className="font-serif-display text-4xl md:text-5xl lg:text-[68px] leading-[1.02] mt-5">
                Let&apos;s talk about<br /><span className="italic text-[#D4AF37]">your story.</span>
              </h1>
              <p className="text-white/65 mt-6 font-light leading-relaxed max-w-md">
                Based in <span className="text-white">Madison, Wisconsin</span>. Crowned is an
                online studio — no public storefront — serving clients across the country
                through online ordering and photo submission. Reach out — we&apos;ll respond
                personally within 24 hours.
              </p>
              <div className="mt-10 border border-[#D4AF37]/30 bg-[#824937]/15 p-6">
                <p className="overline text-[#D4AF37]">Not Sure Where To Start?</p>
                <p className="font-serif-display text-xl mt-3">Start with the Crowned Intake.</p>
                <p className="text-white/60 text-sm mt-2 font-light">
                  A quick, pre-purchase questionnaire that helps us recommend the right Crowned experience for your story.
                </p>
                <button type="button" onClick={() => openIntake("contact-page")} data-testid="contact-intake-btn"
                  className="mt-5 inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all">
                  Start My Crowned Intake →
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} data-testid="contact-form"
              className="lg:col-span-7 bg-[#111111] border border-white/10 p-8 md:p-10 space-y-5 self-start">
              <div className="grid sm:grid-cols-2 gap-5">
                <Fld label="Name"><Input required data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" /></Fld>
                <Fld label="Email"><Input required type="email" data-testid="contact-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" /></Fld>
              </div>
              <Fld label="Phone (optional)">
                <Input data-testid="contact-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000" />
              </Fld>
              <div className="space-y-2.5">
                <Label className="overline text-white/60">Reason for contact</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="contact-reason" className="bg-[#080808] border-white/15 text-white h-14 rounded-none focus:ring-1 focus:ring-[#D4AF37]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111111] border-white/15 text-white rounded-none">
                    {REASONS.map((r) => <SelectItem key={r} value={r} className="focus:bg-[#824937]/30 focus:text-[#D4AF37]">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Fld label="Message"><Textarea required rows={6} data-testid="contact-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us about your project…" /></Fld>
              <button type="submit" disabled={busy} data-testid="contact-submit"
                className="inline-flex w-full items-center justify-center gap-3 bg-[#D4AF37] text-black py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Message</>}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div className="space-y-2.5">
      <Label className="overline text-white/60">{label}</Label>
      <div className="[&>input]:bg-[#080808] [&>input]:border-white/15 [&>input]:h-14 [&>input]:rounded-none [&>input]:text-white [&>input]:focus-visible:ring-1 [&>input]:focus-visible:ring-[#D4AF37] [&>input]:focus-visible:border-[#D4AF37] [&>textarea]:bg-[#080808] [&>textarea]:border-white/15 [&>textarea]:rounded-none [&>textarea]:text-white [&>textarea]:focus-visible:ring-1 [&>textarea]:focus-visible:ring-[#D4AF37] [&>textarea]:focus-visible:border-[#D4AF37]">
        {children}
      </div>
    </div>
  );
}
